import React, { useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ShieldAlert, AlertTriangle, CheckCircle2, FileArchive } from "lucide-react";

/**
 * RestoreDialog
 * -------------
 * 3-Stufen-Wizard zum Wiederherstellen eines Mandanten aus einer Backup-ZIP:
 *   1. Datei wählen + Vorschau holen (POST /admin/restore-preview)
 *   2. Bestätigungs-Wort „RESTORE" eintippen
 *   3. Apply (POST /admin/restore-apply) — überschreibt alle Daten des Mandanten
 *
 * Modus: ausschließlich „Vollständig ersetzen" (sicherer Default,
 * keine Merge-Ambiguität).
 */
export default function RestoreDialog({ open, onOpenChange, activeTenant, onDone }) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [confirm, setConfirm] = useState("");
    const [applyBusy, setApplyBusy] = useState(false);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setConfirm("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(null);
        setPreviewBusy(true);
        try {
            const fd = new FormData();
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("backup_file", f);
            const r = await api.post("/admin/restore-preview", fd);
            setPreview(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Backup-Datei konnte nicht gelesen werden");
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
        } finally {
            setPreviewBusy(false);
        }
    };

    const runApply = async () => {
        if (!file || confirm.trim().toUpperCase() !== "RESTORE") return;
        setApplyBusy(true);
        try {
            const fd = new FormData();
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("bestaetigung", confirm.trim().toUpperCase());
            fd.append("backup_file", file);
            const r = await api.post("/admin/restore-apply", fd);
            const ins = r.data.eingespielt || {};
            toast.success(
                `Restore erfolgreich: ${ins.titles || 0} Titel, ${ins.movements || 0} Bewegungen, `
                + `${ins.sonder_wb_events || 0} Sonder-WB, ${ins.bilanz_snapshots || 0} Snapshots wiederhergestellt`,
                { duration: 6000 },
            );
            reset();
            onOpenChange(false);
            if (onDone) onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Restore fehlgeschlagen");
        } finally {
            setApplyBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) reset();
                onOpenChange(o);
            }}
        >
            <DialogContent data-testid="restore-dialog" className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileArchive className="h-5 w-5 text-amber-300" />
                        Daten aus Backup wiederherstellen
                    </DialogTitle>
                    <DialogDescription>
                        Spielt eine zuvor exportierte Backup-ZIP für{" "}
                        <strong className="text-foreground">{activeTenant?.name}</strong> ein.{" "}
                        <strong className="text-red-300">
                            Alle aktuellen Daten dieses Mandanten werden vorher gelöscht.
                        </strong>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Schritt 1: Datei wählen */}
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                            1. Backup-ZIP wählen
                        </Label>
                        <Input
                            ref={fileRef}
                            type="file"
                            accept=".zip,application/zip"
                            onChange={handleFile}
                            data-testid="restore-file-input"
                        />
                        {previewBusy && (
                            <div className="text-xs text-muted-foreground">Backup wird gelesen …</div>
                        )}
                    </div>

                    {/* Vorschau */}
                    {preview && (
                        <div
                            className="p-3 rounded-md border border-border bg-surface2 space-y-2 text-sm"
                            data-testid="restore-preview"
                        >
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Vorschau Backup-Inhalt
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                <div>Quelle-Mandant:</div>
                                <div className="font-mono">{preview.quelle_tenant_id}</div>
                                <div>Erstellt am:</div>
                                <div className="font-mono">
                                    {preview.erstellt_am
                                        ? new Date(preview.erstellt_am).toLocaleString("de-DE")
                                        : "—"}
                                </div>
                                <div>Titel:</div>
                                <div className="tabular">{preview.anzahl_titel}</div>
                                <div>Bewegungen:</div>
                                <div className="tabular">{preview.anzahl_bewegungen}</div>
                                <div>Sonder-WB:</div>
                                <div className="tabular">{preview.anzahl_sonder_wb}</div>
                                <div>Snapshots:</div>
                                <div className="tabular">{preview.anzahl_snapshots}</div>
                            </div>

                            {preview.tenant_mismatch && (
                                <div className="p-2 mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                    <div>
                                        Das Backup stammt vom Mandanten{" "}
                                        <strong>{preview.quelle_tenant_id}</strong> — Sie wollen es in{" "}
                                        <strong>{preview.ziel_tenant_id}</strong> einspielen. Beim Restore
                                        werden alle IDs neu vergeben und die Daten werden korrekt unter
                                        dem Ziel-Mandanten abgelegt.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Schritt 2: Bestätigung */}
                    {preview && (
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                2. Zur Bestätigung „RESTORE" eintippen
                            </Label>
                            <Input
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                placeholder="RESTORE"
                                className="font-mono"
                                data-testid="restore-confirm-input"
                            />
                        </div>
                    )}

                    {preview && confirm.trim().toUpperCase() === "RESTORE" && (
                        <div className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm flex items-start gap-2">
                            <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
                            <div>
                                <div className="font-medium">Letzte Warnung</div>
                                Beim Klick auf „Wiederherstellen" werden{" "}
                                <strong>alle aktuellen Daten</strong> von{" "}
                                <strong>{activeTenant?.name}</strong> gelöscht und durch das Backup
                                ersetzt. Dieser Vorgang ist nicht rückgängig zu machen.
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={applyBusy}
                        data-testid="restore-cancel"
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={runApply}
                        disabled={
                            applyBusy
                            || !preview
                            || confirm.trim().toUpperCase() !== "RESTORE"
                        }
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="restore-apply-btn"
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        {applyBusy ? "Stelle wieder her …" : "Wiederherstellen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
